import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Youtube, Instagram, Twitter, Facebook, Linkedin } from 'lucide-react';
import { personalInfo, socialLinks } from '../data/mockData';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Work', path: '/work' }
  ];

  const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'youtube': return <Youtube size={18} />;
      case 'instagram': return <Instagram size={18} />;
      case 'twitter': return <Twitter size={18} />;
      case 'facebook': return <Facebook size={18} />;
      case 'linkedin': return <Linkedin size={18} />;
      default: return null;
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0f1419]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Links to Gallery section on About page */}
          <Link to="/about#gallery" className="flex items-center space-x-2">
            <span className="font-serif text-2xl font-bold text-[#f5f5f0] tracking-tight">
              JFB
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative font-medium text-sm tracking-wide uppercase transition-colors duration-300 ${
                  location.pathname === link.path
                    ? 'text-[#d4af37]'
                    : 'text-[#f5f5f0]/80 hover:text-[#d4af37]'
                }`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#d4af37]" />
                )}
              </Link>
            ))}
          </nav>

          {/* Social Icons - Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            {socialLinks.slice(0, 4).map((social) => (
              <a
                key={social.platform}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#f5f5f0]/60 hover:text-[#d4af37] transition-colors duration-300"
                aria-label={social.platform}
              >
                {getSocialIcon(social.platform)}
              </a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-[#f5f5f0] p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed inset-0 top-20 bg-[#0f1419]/98 backdrop-blur-lg transition-transform duration-300 ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <nav className="flex flex-col items-center justify-center h-full space-y-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMenuOpen(false)}
              className={`text-2xl font-serif font-medium tracking-wide transition-colors duration-300 ${
                location.pathname === link.path
                  ? 'text-[#d4af37]'
                  : 'text-[#f5f5f0] hover:text-[#d4af37]'
              }`}
            >
              {link.name}
            </Link>
          ))}
          
          {/* Social Icons - Mobile */}
          <div className="flex items-center space-x-6 pt-8">
            {socialLinks.slice(0, 4).map((social) => (
              <a
                key={social.platform}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#f5f5f0]/60 hover:text-[#d4af37] transition-colors duration-300"
                aria-label={social.platform}
              >
                {getSocialIcon(social.platform)}
              </a>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;